import { EXPIRY_UNIT_TYPE } from "@/constants/expiry-reason-constant";
import { COLUMN_FRI, COLUMN_MON, COLUMN_SAT, COLUMN_SUN, COLUMN_THU, COLUMN_TUE, COLUMN_WED } from "../constants/column-types";
import { REPORTS_FILTER_DATE_TIME_CURRENT_MONTH, REPORTS_FILTER_DATE_TIME_CURRENT_WEEK, REPORTS_FILTER_DATE_TIME_LAST_FOUR_WEEKS, REPORTS_FILTER_DATE_TIME_LAST_MONTH, REPORTS_FILTER_DATE_TIME_LAST_SIX_MONTHS, REPORTS_FILTER_DATE_TIME_LAST_THREE_DAYS, REPORTS_FILTER_DATE_TIME_LAST_TWO_WEEKS, REPORTS_FILTER_DATE_TIME_LAST_WEEK, REPORTS_FILTER_DATE_TIME_LAST_YEAR, REPORTS_FILTER_DATE_TIME_TODAY, REPORTS_FILTER_DATE_TIME_YESTERDAY,
    FILTER_INACTIVE_DAYS_30, FILTER_INACTIVE_DAYS_60, FILTER_INACTIVE_DAYS_90, FILTER_INACTIVE_DAYS_180, FILTER_INACTIVE_DAYS_365} from "../constants/element-type";
import { DAYS_OF_THE_WEEK, RECURRENCE_OPTION } from "../constants/notification-key";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Translator } from "../contexts/translator";
import { MultiSelectOption } from "../models/MultiSelectOption";
import { convertDateToString } from "../services/utilities/dateFormatting";
import { getGlobalTranslator } from "@/services/utilities/ServiceError";

export const getDateWithFullTime = (): string => {
    const date = new Date();

    let day: string = date.getDate().toString().padStart(2, '0');
    let month: string = (date.getMonth() + 1).toString().padStart(2, '0');
    let hours: string = date.getHours().toString().padStart(2, '0');
    let minutes: string = date.getMinutes().toString().padStart(2, '0');
    let seconds: string = date.getSeconds().toString().padStart(2, '0');

    return `${day}/${month}/${date.getFullYear()} ${hours}:${minutes}:${seconds}`;
};


/**
 * Transform incoming date object to a number recognised by the api
 * @param date as <Date> "2021-05-04T14:27:40.000Z"
 * @returns date as number 20210504
 */
export const convertDateToNumber = (date) => {
    let definedDate = new Date(Date.now());
    if (date) {
        definedDate = date;
    }
    const day = definedDate.getDate().toString().padStart(2, '0');
    const month = (definedDate.getMonth() + 1).toString().padStart(2, '0');
    const year = definedDate.getFullYear();

    return +`${year}${month}${day}`;
}

export const convertNumberToDate = (dateNumber) => {
    const year = Math.round((dateNumber / 10000));
    const month = Math.round((dateNumber - (year * 10000)) / 100);
    const day = dateNumber - (year * 10000) - (month * 100);

    const newDate = new Date(year, (month - 1), day);
    return isValidDate(newDate) ? newDate : new Date(Date.now());
}

/**
 * Converts an ISO date string into a human-readable format.
 *
 * Input format: "YYYY-MM-DDTHH:MM:SS.sss"
 * Output format: "Weekday, Month Day, Year at HH:MM AM/PM"
 *
 * Example:
 *   formatReadableDate("2025-04-29T09:50:29.000")
 *   => "Tuesday, April 29, 2025 at 9:50 AM"
 *
 * @param date - A date string in ISO 8601 format
 * @returns A human-readable formatted date string
 */
export const convertDateToHumanDate = (date: string): string => {
    const parsedDate = new Date(date);

    const options: Intl.DateTimeFormatOptions = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    };

    return parsedDate.toLocaleString(undefined, options).replace(',', ' at');
};


export const isValidDate = (date) => {
    return date instanceof Date && !isNaN(date.valueOf());
}

/**
 * Get today's date.
 * @param applyFormatting - if not supplied we default to true (YYYY-MM-DD). If false, we return the date without formatting (YYYYMMDD).
 * @returns 
 */
export const getTodayDate = (applyFormatting?: boolean | undefined): string => {
    const date = new Date();
    const month = date.getMonth() < 9 ? `0${(date.getMonth() + 1)}` : (date.getMonth() + 1);
    const day = date.getDate() < 10 ? `0${date.getDate()}` : (date.getDate());
    const seperator = (applyFormatting === undefined || applyFormatting === true) ? "-" : "";
    return date.getFullYear() + seperator + month + seperator + day;
}

export const getDayNames = (translate: Translator): MultiSelectOption[] => {
    return [
        {
            label: translate.byKey(TranslationKeys.monday),
            value: "1",
        },
        {
            label: translate.byKey(TranslationKeys.tuesday),
            value: "2",
        },
        {
            label: translate.byKey(TranslationKeys.wednesday),
            value: "3",
        },
        {
            label: translate.byKey(TranslationKeys.thursday),
            value: "4",
        },
        {
            label: translate.byKey(TranslationKeys.friday),
            value: "5",
        },
        {
            label: translate.byKey(TranslationKeys.saturday),
            value: "6",
        },
        {
            label: translate.byKey(TranslationKeys.sunday),
            value: "7",
        },
        {
            label: translate.byKey(TranslationKeys.holiday),
            value: "9",
        },
    ];
};

export const getDateForPreviousThirtyDays= (): string => {
    const date = new Date();
    const previous = new Date(date.setDate(date.getDate() - 29));
    const month = previous.getMonth() < 9 ? `0${(previous.getMonth() + 1)}` : (previous.getMonth() + 1);
    const day = previous.getDate() < 10 ? `0${previous.getDate()}` : (previous.getDate());

    return date.getFullYear() + "-" + month + "-" + day;
}

export const buildFirstInLastOutWeekDays = (firstInLastOutReport) => {
    const daysOfTheCurrentWeek = getCurrentDaysOfWeek();
    const data = Object.keys(firstInLastOutReport).map((key) => {
        return {
            day: key.replace('day', ''),
            time: firstInLastOutReport[key],
            accessor: key
        };
    });

    const currentWeekReportDays = data.filter((item) => {
        if (daysOfTheCurrentWeek.includes(parseInt(item.day))) return item;
    });

    return currentWeekReportDays;
};

export const buildFirstInLastOutWeekDaysColumns = () => {
    const daysOfTheCurrentWeek = getCurrentDaysOfWeek();

    const data = Object.keys(daysOfTheCurrentWeek).map((key) => {
        return {
            day: parseInt(daysOfTheCurrentWeek[key]) < 10 ? '0' + daysOfTheCurrentWeek[key] : (daysOfTheCurrentWeek[key]).toString(),
            accessor: parseInt(daysOfTheCurrentWeek[key]) < 10 ? 'day0' + daysOfTheCurrentWeek[key] : 'day' + daysOfTheCurrentWeek[key]
        };
    });
    return data;
}


export const getCurrentDaysOfWeek = () => {
    const startDay = 1; // 0=Sun; 1=Mon and so on.
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const firstDayOfWeek = (currentDate.getDate() - (7 + currentDate.getDay() - startDay) % 7);

    let builtDays:number[] = [];
    let dayOfWeek = new Date();
    dayOfWeek.setDate(firstDayOfWeek)
    for (var i = 0; i < 7; i++) {
        const month = dayOfWeek.getMonth();
        if (month === currentMonth) {
            builtDays.push(dayOfWeek.getDate());
        }
        dayOfWeek.setDate(dayOfWeek.getDate() + 1)
    }

    return builtDays;
}

/*
  Return the date and time without the T in between and without the milliseconds.
*/
export const getDateTimeWithoutMillis = (dateTimeVal: string): string => {
    let dateTime = dateTimeVal.replace("T", " ").replace(".000", "");
    const index = dateTime.lastIndexOf('.');

    if (index !== -1) {
        dateTime = dateTime.substring(0, index);
    }

    return dateTime;
}

// convert date to YYYY/MM/DD
export function formatDate(inputDate: string): string {
    const date = new Date(inputDate);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}/${month}/${day}`;
}

export const getDaysOfTheWeek = (translate: any): MultiSelectOption[] => {
    return [
        { value: DAYS_OF_THE_WEEK.MONDAY, label: translate.byKey(TranslationKeys.monday) },
        { value: DAYS_OF_THE_WEEK.TUESDAY, label: translate.byKey(TranslationKeys.tuesday) },
        { value: DAYS_OF_THE_WEEK.WEDNESDAY, label: translate.byKey(TranslationKeys.wednesday) },
        { value: DAYS_OF_THE_WEEK.THURSDAY, label: translate.byKey(TranslationKeys.thursday) },
        { value: DAYS_OF_THE_WEEK.FRIDAY, label: translate.byKey(TranslationKeys.friday) },
        { value: DAYS_OF_THE_WEEK.SATURDAY, label: translate.byKey(TranslationKeys.saturday) },
        { value: DAYS_OF_THE_WEEK.SUNDAY, label: translate.byKey(TranslationKeys.sunday) },
    ];
};

export const getDayColumnId = (inputDate: string): string => {
    let columnIdName = "";
    switch (inputDate) {
        case "mon" :
            columnIdName = COLUMN_MON;
        break;
        case "tue" :
            columnIdName = COLUMN_TUE;
        break;
        case "wed" :
            columnIdName = COLUMN_WED;
        break;
        case "thu" :
            columnIdName = COLUMN_THU;
        break;
        case "fri" :
            columnIdName = COLUMN_FRI;
        break;
        case "sat" :
            columnIdName = COLUMN_SAT;
        break;
        case "sun" :
            columnIdName = COLUMN_SUN;
        break;
    }
    return columnIdName;
}

export const getMonthsOfTheYear = (translate: any): MultiSelectOption[] => {
    return [
        {value:"JAN" , label: translate.byKey(TranslationKeys.january)},
        {value:"FEB" , label: translate.byKey(TranslationKeys.february)},
        {value:"MAR" , label: translate.byKey(TranslationKeys.march)},
        {value:"APR" , label: translate.byKey(TranslationKeys.april)},
        {value:"MAY" , label: translate.byKey(TranslationKeys.may)},
        {value:"JUN" , label: translate.byKey(TranslationKeys.june)},
        {value:"JUL" , label: translate.byKey(TranslationKeys.july)},
        {value:"AUG" , label: translate.byKey(TranslationKeys.august)},
        {value:"SEP" , label: translate.byKey(TranslationKeys.september)},
        {value:"OCT" , label: translate.byKey(TranslationKeys.october)},
        {value:"NOV" , label: translate.byKey(TranslationKeys.november)},
        {value:"DEC" , label: translate.byKey(TranslationKeys.december)},
    ];
};

export const getRecurrenceOptionss = (translate: any): MultiSelectOption[] => {
    return [
        { value: RECURRENCE_OPTION.FIRST, label: translate.byKey(TranslationKeys.first) },
        { value: RECURRENCE_OPTION.SECOND, label: translate.byKey(TranslationKeys.second) },
        { value: RECURRENCE_OPTION.THIRD, label: translate.byKey(TranslationKeys.third) },
        { value: RECURRENCE_OPTION.FOURTH, label: translate.byKey(TranslationKeys.fourth) },
        { value: RECURRENCE_OPTION.LAST, label: translate.byKey(TranslationKeys.last) },
    ];
};

export const processSelectedDates = (selected: MultiSelectOption[], translate: Translator) : MultiSelectOption[] => {
    let compiledDates: MultiSelectOption[] = [];
    const date = new Date();
    selected.map((item) => {
        let dates: string = "";
        let label: string = "";

        if (item.elementSysname === REPORTS_FILTER_DATE_TIME_CURRENT_MONTH) {
            dates = getCurrentMonthDates(date);
            label = translate.byKey(TranslationKeys.currentMonth);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_CURRENT_WEEK) {
            dates = getCurrentWeekDates(date);
            label = translate.byKey(TranslationKeys.currentWeek);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_TODAY) {
            dates = getTodayDate();
            label = translate.byKey(TranslationKeys.today);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_YESTERDAY) {
           dates = getYesterdayDate(date);
           label = translate.byKey(TranslationKeys.yesterday);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_LAST_WEEK) {
            dates = getLastXWeeksDates(date, 1);
            label = translate.byKey(TranslationKeys.lastWeek);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_LAST_YEAR) {
            dates = getLastYearDates(date);
            label = translate.byKey(TranslationKeys.lastYear);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_LAST_MONTH) {
            dates = getLastXMonthDates(date, 1);
            label = translate.byKey(TranslationKeys.lastMonth);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_LAST_THREE_DAYS) {
            dates = getStartAndEndForLastXDays(date, 3);
            label = translate.byKey(TranslationKeys.lastThreeDays);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_LAST_TWO_WEEKS) {
            dates = getLastXWeeksDates(date, 2);
            label = translate.byKey(TranslationKeys.lastTwoWeeks);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_LAST_FOUR_WEEKS) {
            dates = getLastXWeeksDates(date, 4);
            label = translate.byKey(TranslationKeys.lastFourWeeks);
        }
        else if (item.elementSysname === REPORTS_FILTER_DATE_TIME_LAST_SIX_MONTHS) {
            dates = getLastXMonthDates(date, 6);
            label = translate.byKey(TranslationKeys.lastSixMonths);
        }

        if (label !== "" && dates !== "") {
            compiledDates.push({
                label: label,
                value: dates,
                settingDataId: item.settingDataId,
                elementSysname: item.elementSysname
            })
        }
    })
    
    return compiledDates.length !== 0 ? compiledDates : selected;
}

export const processSelectedInactivePeriods = (selected: MultiSelectOption[], translate: Translator): MultiSelectOption[] => {
    let compiledPeriods: MultiSelectOption[] = [];
    selected.map((item) => {
        let label: string = "";

        if (item.elementSysname === FILTER_INACTIVE_DAYS_30) {
            label = translate.byKey(TranslationKeys.inactiveFor30Days);
        }
        else if (item.elementSysname === FILTER_INACTIVE_DAYS_60) {
            label = translate.byKey(TranslationKeys.inactiveFor60Days);
        }
        else if (item.elementSysname === FILTER_INACTIVE_DAYS_90) {
            label = translate.byKey(TranslationKeys.inactiveFor90Days);
        }
        else if (item.elementSysname === FILTER_INACTIVE_DAYS_180) {
            label = translate.byKey(TranslationKeys.inactiveFor180Days);
        }
        else if (item.elementSysname === FILTER_INACTIVE_DAYS_365) {
            label = translate.byKey(TranslationKeys.inactiveFor365Days);
        }

        if (label !== "") {
            compiledPeriods.push({
                label: label,
                value: item.value,
                settingDataId: item.settingDataId,
                elementSysname: item.elementSysname
            })
        }
    })

    return compiledPeriods.length !== 0 ? compiledPeriods : selected;
}

const getCurrentMonthDates = (date: Date): string => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

    return `${convertDateToString(firstDay).replaceAll("/", "-")};${convertDateToString(lastDay).replaceAll("/", "-")}`;
}

export const getCurrentWeekDates = (date: Date): string => {
    const firstDay = new Date(date.setDate(date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1)));
    const lastDay = new Date(date.setDate(date.getDate() - date.getDay() + 7));
    
    return `${convertDateToString(firstDay).replaceAll("/", "-")};${convertDateToString(lastDay).replaceAll("/", "-")}`;
}

export const getAllCurrentWeekDates = (date: Date): string => {
    const dates: string[] = [];
    const firstDay = new Date(date.setDate(date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1)));
    const lastDay = new Date(date.setDate(date.getDate() - date.getDay() + 7));

    let currentDate = new Date(firstDay);
    while (currentDate <= lastDay) {
        dates.push(convertDateToString(currentDate).replaceAll("/", "-"));
        currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates.join(",");
}

export const convertDatesToWeekDays = (dates: string, datesToCheckAgainst: string[]): string[] => {
    const dateArray = dates.split(",").map(date => date.trim());
    const dateSet = new Set(dateArray);
    const weekdays: string[] = [];

    datesToCheckAgainst.forEach((currentDate: string) => {
        if (dateSet.has(currentDate)) {
            const date = new Date(currentDate);
            weekdays.push(date.toLocaleString("en-US", { weekday: "long" }).toLowerCase());
        }
    });

    return weekdays;
}

const getYesterdayDate = (date: Date): string => {
    const yesterday = new Date(date)
    yesterday.setDate(date.getDate() - 1);

    return convertDateToString(yesterday).replaceAll("/", "-");
}

const getLastYearDates = (date: Date): string => {
    const year = date.getFullYear() - 1;
    const firstDay = new Date(`${year}-01-01`);
    const lastDay = new Date(`${year}-12-31`);

    return `${convertDateToString(firstDay).replaceAll("/", "-")};${convertDateToString(lastDay).replaceAll("/", "-")}`;
}

const getLastXMonthDates = (date: Date, month: number): string => {
    const lastMonth = date.getMonth() - month;
    
    const firstDayOfLastMonth = new Date(date.getFullYear(), lastMonth, 1);
    const lastDayOfLastMonth = new Date(date.getFullYear(), date.getMonth(), 0);

    return `${convertDateToString(firstDayOfLastMonth).replaceAll("/", "-")};${convertDateToString(lastDayOfLastMonth).replaceAll("/", "-")}`;
}

const getStartAndEndForLastXDays = (date: Date, days: number): string => {
    const firstDay = new Date(date);
    const lastDay = new Date(date);

    firstDay.setDate(date.getDate() - days);
    lastDay.setDate(date.getDate() - 1);

    return `${convertDateToString(firstDay).replaceAll("/", "-")};${convertDateToString(lastDay).replaceAll("/", "-")}`; 
}

const getLastXWeeksDates = (date: Date, weeks: number): string => {
    const firstDay = new Date(date);
    const lastDay = new Date(date);
    
    // Adjust for Monday as the start of the week to match backend
    const dayOfWeek = date.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 6, Monday = 0, Tuesday = 1, etc.
    
    firstDay.setDate(date.getDate() - daysFromMonday - (7 * weeks));
    lastDay.setDate(date.getDate() - daysFromMonday - 1);

    return `${convertDateToString(firstDay).replaceAll("/", "-")};${convertDateToString(lastDay).replaceAll("/", "-")}`;
}

export const getDatesBetween = (startDate: string, endDate: string): string[] => {
    const dates: string[] = [];

    if (startDate && endDate) {
        const date = new Date(startDate);
        const lastDate = new Date(endDate);

        while (date <= lastDate) {
            const dateForFilter = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
            dates.push(dateForFilter);
            date.setDate(date.getDate() + 1);
        }
    }

    return dates;
}

export const getDayOfTheWeek = (day: number, translate: Translator) => {
    switch (day) {
        case 0:
            return translate.byKey(TranslationKeys.sunday);
        case 1:
            return translate.byKey(TranslationKeys.monday);
        case 2:
            return translate.byKey(TranslationKeys.tuesday);
        case 3:
            return translate.byKey(TranslationKeys.wednesday);
        case 4:
            return translate.byKey(TranslationKeys.thursday);
        case 5:
            return translate.byKey(TranslationKeys.friday);
        case 6:
            return translate.byKey(TranslationKeys.saturday);
    }
}

export const getDayName = (dateStr: string): string => {
    if (!dateStr) {
        return '';
    }
    const date = new Date(`${dateStr}T00:00:00`);
    return ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][date.getDay()] ?? '';
};

export const convertMillisecondsToTime = (timeWorkedMilliseconds: number): string => {
    const hours = Math.floor(timeWorkedMilliseconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((timeWorkedMilliseconds % 3600) / 60).toString().padStart(2, '0');
    const seconds = (timeWorkedMilliseconds % 60).toString().padStart(2, '0');

    return `${hours}:${minutes}:${seconds}`;
}

/**
 * For duration we determine whether it's Days/Weeks/Months based on erUnits value.
 * For durationWarn we always use Days as the unit of measurement. With zero meaning its disabled. I.e. no warning.
 * @param duration
 * @param unit
 */
export const convertDurationByUnits = (duration: number, unit: number) => {
    if (duration === 0) {
        return "-";
    }

    switch (unit) {
        case EXPIRY_UNIT_TYPE.DAYS:
            return getGlobalTranslator().byKeyFormatted(TranslationKeys.daysWithCount, { count: duration });
        case EXPIRY_UNIT_TYPE.WEEKS:
            return getGlobalTranslator().byKeyFormatted(TranslationKeys.weeksWithCount, { count: duration });
        case EXPIRY_UNIT_TYPE.MONTHS:
            return getGlobalTranslator().byKeyFormatted(TranslationKeys.monthsWithCount, { count: duration });
        default:
            return "-";
    }
}